import { useEffect } from 'react';

interface SwipeOptions {
  threshold?: number;
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  disabled?: boolean;
}

function getImageZoomScale(element: HTMLElement): number {
  const img = element.querySelector('img');
  if (!img) return 1;

  const style = window.getComputedStyle(img);
  const transform = style.transform;

  if (transform === 'none') return 1;

  const matrix = transform.match(/matrix\((.+)\)/);
  if (!matrix) return 1;

  const values = matrix[1].split(', ');
  const scaleX = parseFloat(values[0]);

  return scaleX;
}

export function useSwipeGesture(
  ref: React.RefObject<HTMLElement>,
  { threshold = 40, onSwipeLeft, onSwipeRight, disabled }: SwipeOptions
): void {
  useEffect(() => {
    if (!ref.current || disabled) return;

    let startX = 0;
    let startY = 0;
    let touchCount = 0;

    const handleTouchStart = (e: TouchEvent) => {
      touchCount = e.touches.length;
      if (touchCount === 1) {
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (e.changedTouches.length === 0 || touchCount !== 1) return;

      const endX = e.changedTouches[0].clientX;
      const endY = e.changedTouches[0].clientY;

      const deltaX = endX - startX;
      const deltaY = endY - startY;

      const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      const isSignificant = Math.abs(deltaX) > threshold;

      const zoomScale = getImageZoomScale(ref.current!);
      const isZoomedIn = zoomScale > 1.05;

      if (!isZoomedIn && isHorizontalSwipe && isSignificant) {
        if (deltaX > 0 && onSwipeRight) {
          onSwipeRight();
        } else if (deltaX < 0 && onSwipeLeft) {
          onSwipeLeft();
        }
      }
    };

    const element = ref.current;
    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('touchend', handleTouchEnd);

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [ref, threshold, onSwipeLeft, onSwipeRight, disabled]);
}
